import type { Modifier } from '@dnd-kit/core';
import { getEventCoordinates } from '@dnd-kit/utilities';
import Decimal from 'decimal.js';

import { getDateFnsLocale } from './date';
import { getStore } from './reduxStore';

// re-use canvas object for performance reasons
const textWidthCanvas: HTMLCanvasElement = document.createElement('canvas');
export const getTextWidth = (text: string, font: string) => {
  const context = textWidthCanvas.getContext('2d') as CanvasRenderingContext2D;

  context.font = font;
  return context.measureText(text).width;
};

export const isValidEmailAddress = (email: string) => {
  // eslint-disable-next-line max-len
  const re =
    /^(([^<>()[\]\\.,;:\s@"]+(\.[^<>()[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/;
  return re.test(String(email).toLowerCase());
};

export const sortAlphabeticallyBy = <
  P extends string,
  T extends {
    readonly [K in P]: string;
  }
>(
  data: T[],
  objectProp: P,
  ignorePunctuation = true
) =>
  data.sort((a, b) => {
    if (
      (!(objectProp in a) || !(objectProp in b)) &&
      typeof a !== 'string' &&
      typeof b !== 'string'
    )
      return 0;
    return a[objectProp].localeCompare(b[objectProp], undefined, {
      ignorePunctuation
    });
  });

export const toDecimalPlaces = (value: number, dec = 2) =>
  new Decimal(value).toDecimalPlaces(dec).toNumber();

export const displayRoundedNumber = (value: number): string =>
  new Intl.NumberFormat(getDateFnsLocale().code, {
    minimumFractionDigits: 0,
    maximumFractionDigits: 0
  }).format(value);

export const displayNumber = (value: number, decimals = 2): string =>
  new Intl.NumberFormat(getDateFnsLocale().code, {
    minimumFractionDigits: decimals,
    maximumFractionDigits: decimals
  }).format(value); // the default to display for the user is 2 decimals

export const displayFlooredNumber = (value: number, decimals = 2): string => {
  const power = Math.pow(10, decimals);
  const roundedValue = Math.floor(value * power) / power;

  return displayNumber(roundedValue, decimals);
};

export const displayCurrency = (value: number) => {
  const localeCode = getDateFnsLocale().code;

  return new Intl.NumberFormat(localeCode, {
    style: 'currency',
    currency: 'EUR'
  }).format(value);
};

export const displayPercentage = (number: number, decimals = 2) =>
  new Intl.NumberFormat(getDateFnsLocale().code, {
    style: 'percent',
    minimumFractionDigits: decimals,
    maximumFractionDigits: decimals
  }).format(number);

export const firstLetterToUpperCase = (str: string) =>
  str.charAt(0).toUpperCase() + str.slice(1);

export const brightness = (hexColor: `#${string}`, percent: number) => {
  let hex = hexColor.replace(/^\s*#|\s*$/g, '');

  if (hex.length === 3) {
    hex = hex.replace(/(.)/g, '$1$1');
  }

  const r = parseInt(hex.substring(0, 2), 16);
  const g = parseInt(hex.substring(2, 4), 16);
  const b = parseInt(hex.substring(4, 6), 16);

  return (
    '#' +
    `${(0 | ((1 << 8) + r + (256 - r) * (percent / 100)))
      .toString(16)
      .substring(1)}` +
    `${(0 | ((1 << 8) + g + (256 - g) * (percent / 100)))
      .toString(16)
      .substring(1)}` +
    `${(0 | ((1 << 8) + b + (256 - b) * (percent / 100)))
      .toString(16)
      .substring(1)}`
  );
};

// TODO: after migration is done, change selectors #aysist_Root, #message_box, #notificationMessage, etc:
const hiddenForPrintDefaultSelectors =
  '#aysist_Root, #aysist_Root ~ *, #message_box, #notificationMessage';
export const printContent = (
  content: Node,
  hiddenForPrintSelectors?: string
) => {
  const elementsToHide = Array.from(
    document.querySelectorAll(
      `${hiddenForPrintDefaultSelectors} ${
        hiddenForPrintSelectors ? `, ${hiddenForPrintSelectors}` : ''
      }`
    )
  ) as HTMLElement[];
  elementsToHide.forEach((elem) => {
    elem.style.display = 'none';
  });

  const contentClone = content.cloneNode(true);
  document.body.appendChild(contentClone);
  document.body.classList.add('print-preview');
  window.print();
  document.body.classList.remove('print-preview');
  document.body.removeChild(contentClone);
  elementsToHide.forEach((elem) => {
    elem.style.display = '';
  });
};

export const getCurrentlyLoggedInEmployeeId = () => {
  const { userDetails } = getStore().getState();
  return userDetails.employee?.empId?.toString() || '';
};

export const getCurrentlyLoggedInEmployeeEntityId = () => {
  const { userDetails } = getStore().getState();
  return userDetails.employee?.entityId?.toString() || '';
};

export const getCurrentlyLoggedInEmployee = () => {
  const { userDetails } = getStore().getState();
  return userDetails.employee;
};

export const isSDBAccount = () => {
  const { userDetails } = getStore().getState();
  return userDetails.accountId <= 0;
};

export const stringToFloat = (value, originalValue) => {
  return Number(originalValue.toString().replace(',', '.'));
};

export const convertFlagsObjectToArray = <T>(flags: T): string[] => {
  const flagsArr: string[] = [];
  for (const flag in flags) {
    if (flags[flag] === true) {
      flagsArr.push(flag);
    }
  }
  return flagsArr;
};

export const convertFlagsArrayToObjects = <T>(flags: string[]): T => {
  const convertedFlags = {} as T;
  flags.forEach((item) => {
    convertedFlags[`${item}`] = true;
  });
  return convertedFlags;
};

export const hexToRGB = (hex: string, alpha?: number) => {
  const r = parseInt(hex.slice(1, 3), 16);
  const g = parseInt(hex.slice(3, 5), 16);
  const b = parseInt(hex.slice(5, 7), 16);

  if (alpha) {
    return `rgba(${r}, ${g}, ${b}, ${alpha})`;
  }

  return `rgb(${r}, ${g}, ${b})`;
};

// Custom DnD-kit cursor modifier
export const snapTopLeftToCursor: Modifier = ({
  activatorEvent,
  draggingNodeRect,
  transform
}) => {
  if (draggingNodeRect && activatorEvent) {
    const activatorCoordinates = getEventCoordinates(activatorEvent);

    if (!activatorCoordinates) return transform;

    const offsetX = activatorCoordinates.x - draggingNodeRect.left;
    const offsetY = activatorCoordinates.y - draggingNodeRect.top;

    return { ...transform, x: transform.x + offsetX, y: transform.y + offsetY };
  }

  return transform;
};

export const clipText = (text: string, maxLength: number) => {
  return text.length > maxLength ? `${text.slice(0, maxLength)} ...` : text;
};
